import Microgame from '../engine/Microgame.js';

export default class ArcheryGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Tir à l'arc !`, en: `Archery!`, es: `¡Tiro con arco!` },
            controls: { fr: `Glisse en arrière pour bander l'arc, relâche pour tirer`, en: `Drag back to draw, release to shoot`, es: `Arrastra hacia atrás y suelta para disparar` },
            objective: { fr: `Touche la cible lumineuse avec la flèche`, en: `Hit the glowing target with the arrow`, es: `Golpea el objetivo brillante con la flecha` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M14 2 2 14M22 22 10 10"/><path d="m2 14 1 1.5c1.42 2.14 3.73 3.5 6.32 3.5h0M14 2l1.5 1c2.14 1.42 3.5 3.73 3.5 6.32v0"/></svg>`,
            actions: [
                { type: 'wait', duration: 760 },
                { type: 'tap', x: 50, y: 84, duration: 140 },
                { type: 'wait', duration: 420 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.target = null;
        this.bow = null;
        this.arrow = null;
        this.state = 'aiming';

        const speedMult = engine.speedMultiplier || 1.0;
        this.targetX = 50;

        // Scale target speed so it passes the center even on high difficulties, 
        // but soften the curve: speed at x4 equals old speed at x3.5
        const effectiveMult = 1.0 + (speedMult - 1) * (2.5 / 3.0);
        this.targetSpeed = 300 * effectiveMult;

        this.direction = 1;

        // More generous base time with softened curve
        const adjustedSpeed = 1 + (speedMult - 1) * (1.2 / 3);
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "SHOOT!";

        // Random Start
        this.targetX = this.isPlayback ? 50 : 20 + Math.random() * 60; // 20-80%
        this.direction = this.isPlayback ? 1 : (Math.random() < 0.5 ? 1 : -1);

        // Target (Secondary)
        this.target = document.createElement('div');
        this.target.style.position = 'absolute';
        this.target.style.width = '90px'; // Increased target size
        this.target.style.height = '90px';
        this.target.style.border = `5px solid ${this.secondaryColor}`;
        this.target.style.borderRadius = '50%';
        this.target.style.top = '20%';
        this.target.style.left = `${this.targetX}%`;
        this.target.style.transform = 'translate(-50%, -50%)';
        this.target.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.container.appendChild(this.target);

        // Inner ring
        const inner = document.createElement('div');
        inner.style.position = 'absolute';
        inner.style.width = '45px'; // Increased inner size
        inner.style.height = '45px';
        inner.style.background = this.secondaryColor;
        inner.style.borderRadius = '50%';
        inner.style.top = '50%';
        inner.style.left = '50%';
        inner.style.transform = 'translate(-50%, -50%)';
        this.target.appendChild(inner);

        // Bow (Primary)
        this.bow = document.createElement('div');
        this.bow.style.position = 'absolute';
        this.bow.style.width = '100px';
        this.bow.style.height = '40px';
        this.bow.style.borderBottom = `4px solid ${this.primaryColor}`;
        this.bow.style.borderRadius = '0 0 50% 50%';
        this.bow.style.bottom = '15%';
        this.bow.style.left = '50%';
        this.bow.style.transform = 'translateX(-50%)';
        this.container.appendChild(this.bow);

        // Arrow
        this.arrow = document.createElement('div');
        this.arrow.style.position = 'absolute';
        this.arrow.style.width = '4px';
        this.arrow.style.height = '60px';
        this.arrow.style.background = this.primaryColor;
        this.arrow.style.bottom = '15%';
        this.arrow.style.left = '50%';
        this.arrow.style.transform = 'translateX(-50%)';
        this.container.appendChild(this.arrow);

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    update(dt) {
        if (!this.active || this.won) return;

        if (this.state === 'aiming') {
            // Move Target
            this.targetX += this.targetSpeed * this.direction * (dt / 1000) / (this.container.clientWidth / 100);

            if (this.targetX > 85) {
                this.targetX = 85;
                this.direction = -1;
            } else if (this.targetX < 15) {
                this.targetX = 15;
                this.direction = 1;
            }
            this.target.style.left = `${this.targetX}%`;
        } else if (this.state === 'shooting') {
            // Move Arrow
            const currentBottom = parseFloat(this.arrow.style.bottom) || 15;
            const newBottom = currentBottom + 200 * (dt / 1000);
            this.arrow.style.bottom = `${newBottom}%`;

            // Check Hit
            if (newBottom > 75 && this.state !== 'hit' && this.state !== 'miss') {
                const targetRect = this.target.getBoundingClientRect();
                const arrowRect = this.arrow.getBoundingClientRect();
                const arrowCenter = arrowRect.left + arrowRect.width / 2;

                // Only hit if the arrow is strictly between the visually drawn borders of the target
                if (arrowCenter >= targetRect.left && arrowCenter <= targetRect.right) {
                    // HIT
                    this.state = 'hit';
                    this.target.style.background = this.primaryColor;
                    this.target.style.boxShadow = `0 0 30px ${this.primaryColor}`;
                    this.arrow.style.bottom = '75%';
                    this.win(); // Immediate Win
                } else if (newBottom > 110) {
                    // MISS
                    this.state = 'miss';
                    this.lose();
                }
            }
        }
    }

    onDown() {
        if (!this.active || this.state !== 'aiming') return;
        this.state = 'shooting';
        this.hideTimer = true; // Stop timer when arrow is shot
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
